import React, { useEffect, useState, useRef } from 'react';
import { View, Text } from '@aws-amplify/ui-react';
import {
  Box,
  Button,
  Switch,
  Typography,
  CircularProgress,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { StageRepository } from '../../../repository/StageRepository';
import { CrmService } from '../../../services/CrmService';
import { CustomTextField, CustomButton } from './styledComponents';
import './CRMConfigV2.css';
import { hasUnsavedChanges } from '../utils/hasUnsavedChanges';
import DealStagesEditor from './DealStagesEditor';
import DealFormEditor from './DealFormEditor';
import StageDispositionsEditor from './StageDispositionsEditor';
import DealCardEditor from './DealCardEditor';
import CustomButtonWithInput from '../utils/CustomButtonWithInput';
import { API, graphqlOperation } from 'aws-amplify';
import {
  createStage,
  createStageDisposition,
  createIntegrationXref,
  updateIntegrationXref,
} from '../../../../graphql/mutations';
import { GamifyToast } from '../../../common/CustomToasts';
import InfoIcon from '@mui/icons-material/Info';
import TextField from '@mui/material/TextField';
import gql from 'graphql-tag';
import { fetchDocuSealTemplates } from '../../settings/components/DocuSealVisibility/docusealHelpers';

const { v4: uuidv4 } = require('uuid');

const CRMConfigV2 = ({
  org,
  setOrg,
  orgID,
  featuresList,
  setFeaturesList,
  stageCategories: externalStageCategories,
  setStageCategories: externalSetStageCategories,
}) => {
  const apiKeyInputRef = useRef(null);
  // Use external state if provided, otherwise use local state
  const [internalStageCategories, setInternalStageCategories] = useState(null);
  const stageCategories = externalStageCategories || internalStageCategories;
  const setStageCategories =
    externalSetStageCategories || setInternalStageCategories;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  console.log('🚀 ~ CRMConfigV2 ~ selectedCategory:', selectedCategory);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [copySelectedCategory, setCopySelectedCategory] = useState(null);
  const [configPage, setConfigPage] = useState('stages'); // enum "stages" | "forms" | "dispositions" (coming soon)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cloningLoading, setCloningLoading] = useState(false);
  const [defaultCategoryName, setDefaultCategoryName] = useState('');
  const [docuSealEnabled, setDocuSealEnabled] = useState(false);
  const [integrationXref, setIntegrationXref] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [docuSealConfig, setDocuSealConfig] = useState({
    apiKey: '',
    defaultSigningEmail: '',
    pdfEnabled: true,
    signOnDevice: false,
    signUsingEmail: true,
    noOrderLinesRequiredForSigning: true,
  });
  const [stageMapping, setStageMapping] = useState({
    awaiting: '',
    completed: '',
    default: '',
  });
  const [docuSealFolders, setDocuSealFolders] = useState([]);

  // Reusable MenuItem styles
  const menuItemStyles = {
    backgroundColor: (theme) => theme.custom.background1,
    '&.Mui-selected': {
      backgroundColor: (theme) => theme.custom.background2,
    },
    '&:hover': {
      backgroundColor: (theme) => theme.custom.background2,
    },
    '&.Mui-selected:hover': {
      backgroundColor: (theme) => theme.custom.background2,
    },
  };

  useEffect(() => {
    if (org?.configParms) {
      try {
        const config = JSON.parse(org.configParms);
        const isEnabled = config.publish?.docuSeal === true;
        setDocuSealEnabled(isEnabled);

        if (isEnabled) {
          if (featuresList?.deal) {
            setDocuSealConfig((prev) => ({
              ...prev,
              apiKey: featuresList.deal.DocuSealAPIKey || '',
              signOnDevice: featuresList.deal.signOnDevice === true,
              signUsingEmail: featuresList.deal.signUsingEmail === true,
              pdfEnabled: featuresList.deal.pdfEnabled !== false, // Default to true if not present
            }));
          }
          loadDocuSealStageMapping();
        }
      } catch (e) {
        console.error('Error parsing configParms in CRMConfigV2', e);
      }
    }
  }, [org, featuresList]);

  // When external stageCategories are already populated on mount (e.g. navigating back),
  // initialize selectedCategory so we don't render with null
  useEffect(() => {
    if (!selectedCategory && stageCategories) {
      const categories = Object.values(stageCategories);
      if (categories.length) {
        const first = ensureCategoryDefaults(categories[0]);
        setSelectedCategory(first);
        setCopySelectedCategory(first);
      }
    }
  }, [stageCategories]);

  useEffect(() => {
    if (isLoading) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const crmService = new CrmService();
        const categories = await crmService.getCategoriesWithDependentKPIs(
          orgID
        );
        // Sort categories alphabetically and ensure defaults
        const sortedCategories = categories.sort((a, b) => {
          if (a.title < b.title) return -1;
          if (a.title > b.title) return 1;
          return 0;
        });

        setStageCategories(
          sortedCategories.reduce((acc, el) => {
            acc[el.id] = ensureCategoryDefaults(el);
            return acc;
          }, {})
        );
        if (sortedCategories.length) {
          // sortedCategories already have defaults applied
          const firstCategoryWithDefaults = ensureCategoryDefaults(
            sortedCategories[0]
          );
          setSelectedCategory(firstCategoryWithDefaults);
          setCopySelectedCategory(firstCategoryWithDefaults);
        }
      } catch (error) {
        console.error('Error fetching stage categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [orgID]);

  useEffect(() => {
    const x = hasUnsavedChanges(copySelectedCategory, selectedCategory);
    setUnsavedChanges(x);
  }, [selectedCategory, copySelectedCategory]);

  useEffect(() => {
    // Load folders whenever the component mounts or orgID changes, if DocuSeal is enabled
    if (docuSealEnabled && orgID) {
      console.log('useEffect triggered - loading DocuSeal folders');
      loadDocuSealFolders();
    }
  }, [docuSealEnabled, orgID]);

  useEffect(() => {
    // Also try to load folders when component first renders if org has DocuSeal configured
    if (orgID && org?.configParms) {
      try {
        const configParms = JSON.parse(org.configParms);
        if (configParms?.publish?.docuSeal) {
          console.log(
            'DocuSeal is enabled in org config, loading folders on mount'
          );
          loadDocuSealFolders();
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [org, orgID]);

  const loadDocuSealStageMapping = async () => {
    try {
      const query = gql`
        query GetIntegrationXref($id: ID!) {
          getIntegrationXref(id: $id) {
            id
            mappingJSON
          }
        }
      `;
      const result = await API.graphql({
        query,
        variables: { id: `${orgID}:docuseal:config` },
      });
      const existingXref = result.data.getIntegrationXref;
      setIntegrationXref(existingXref);

      if (existingXref && existingXref.mappingJSON) {
        const mapping = JSON.parse(existingXref.mappingJSON);
        if (mapping.stageMapping) {
          setStageMapping(mapping.stageMapping);
        }
      }
    } catch (error) {
      console.error('Error loading DocuSeal stage mapping:', error);
    }
  };

  const loadDocuSealFolders = async () => {
    try {
      console.log('Loading DocuSeal folders for orgID:', orgID);
      const templates = await fetchDocuSealTemplates(orgID);
      console.log('Fetched templates:', templates);

      // Extract unique folder names from templates
      const uniqueFolders = [
        ...new Set(templates.map((t) => t.folder || 'Default')),
      ];
      console.log('Unique folders:', uniqueFolders);

      setDocuSealFolders(uniqueFolders.sort());
    } catch (error) {
      console.error('Error loading DocuSeal folders:', error);
      setDocuSealFolders([]);
    }
  };

  const saveDocuSealConfig = async () => {
    try {
      if (!org || !org.configParms) {
        console.error('Org or configParms not available');
        return;
      }

      // Save IntegrationXref
      const xrefInput = {
        mappingJSON: JSON.stringify({ stageMapping }),
      };

      if (integrationXref) {
        // Update
        xrefInput.id = integrationXref.id;
        await API.graphql(
          graphqlOperation(updateIntegrationXref, { input: xrefInput })
        );
      } else {
        // Create
        xrefInput.id = `${orgID}:docuseal:config`;
        xrefInput.orgID = orgID;
        xrefInput.partnerID = '';
        xrefInput.partnerKeyName = '';
        xrefInput.partnerKeyValue = '';
        xrefInput.ourKeyName = '';
        xrefInput.ourKeyValue = '';
        xrefInput.isDeleted = false;
        await API.graphql(
          graphqlOperation(createIntegrationXref, { input: xrefInput })
        );
      }

      // Save Org config
      const newFeatures = JSON.parse(JSON.stringify(featuresList));
      if (!newFeatures.deal) newFeatures.deal = {};

      // Check if there are any Order Lines fields
      const hasOrderLinesFields =
        selectedCategory.flexAttributes?.config &&
        Object.values(selectedCategory.flexAttributes.config).some((form) =>
          form.flexFields?.some((field) => field.type === 'Order Lines')
        );

      newFeatures.deal.DocuSealAPIKey = docuSealConfig.apiKey;
      newFeatures.deal.defaultSigningEmail = `david+docuseal-${orgID}@gogamify.com`;
      newFeatures.deal.signOnDevice = docuSealConfig.signOnDevice;
      newFeatures.deal.signUsingEmail = docuSealConfig.signUsingEmail;
      newFeatures.deal.pdfEnabled = docuSealConfig.pdfEnabled;
      // If there are no Order Lines fields, default to true (no order lines required)
      // If there are Order Lines fields, use the user's setting
      newFeatures.deal.noOrderLinesRequiredForSigning = hasOrderLinesFields
        ? docuSealConfig.noOrderLinesRequiredForSigning
        : true;

      const configParms = JSON.parse(org.configParms);
      configParms.features = newFeatures;

      const updateOrgMutation = gql`
        mutation UpdateOrg($input: UpdateOrgInput!) {
          updateOrg(input: $input) {
            id
            name
            contactName
            contactPhone
            contactEmail
            contactAddress
            contactAddress2
            contactCity
            contactState
            contactZip
            configParms
          }
        }
      `;

      const updateOrgInput = {
        id: org.id,
        name: org.name || '',
        contactName: org.contactName || '',
        contactPhone: org.contactPhone || '',
        contactEmail: org.contactEmail || '',
        contactAddress: org.contactAddress || '',
        contactAddress2: org.contactAddress2 || '',
        contactCity: org.contactCity || '',
        contactState: org.contactState || '',
        contactZip: org.contactZip || '',
        configParms: JSON.stringify(configParms),
      };

      const response = await API.graphql({
        query: updateOrgMutation,
        variables: {
          input: updateOrgInput,
        },
      });

      setFeaturesList(newFeatures);
      setOrg(response.data.updateOrg);

      setSnackbar({
        open: true,
        message: 'DocuSeal configuration saved successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving DocuSeal config:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save DocuSeal configuration.',
        severity: 'error',
      });
    }
  };

  const handleSigningMethodChange = (field, checked) => {
    const { signOnDevice, signUsingEmail } = docuSealConfig;

    if (
      !checked &&
      ((field === 'signOnDevice' && !signUsingEmail) ||
        (field === 'signUsingEmail' && !signOnDevice))
    ) {
      setSnackbar({
        open: true,
        message:
          'At least one signing method (Sign on Device or Sign using Email) must be enabled.',
        severity: 'warning',
      });
      return;
    }

    setDocuSealConfig((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const handleFormUpdates = async (forms) => {
    const newFlexAttributes = {
      ...selectedCategory.flexAttributes,
      config: forms,
    };
    const CRMUpdateObject = {
      ...selectedCategory,
      flexAttributes: newFlexAttributes,
    };
    const submitObject = { ...CRMUpdateObject };
    delete submitObject.dispositions;
    delete submitObject.stages;
    delete submitObject.dependentKPIRules;
    submitObject.flexAttributes = JSON.stringify(submitObject.flexAttributes);
    try {
      await StageRepository.updateStageCategory(submitObject);
      setStageCategories((p) => ({
        ...p,
        [CRMUpdateObject.id]: CRMUpdateObject,
      }));
      setCopySelectedCategory(CRMUpdateObject);
      setSelectedCategory(CRMUpdateObject);
    } catch (err) {
      console.log('ERROR updating CRM', err);
    }
  };

  const handleCRMUpdates = async () => {
    const CRMSubmitObj = {
      id: selectedCategory.id,
      isDefault: selectedCategory.isDefault,
      title: selectedCategory.title,
      enableDeal: selectedCategory.enableDeal,
      enablePindrop: selectedCategory.enablePindrop,
      flexAttributes: JSON.stringify(selectedCategory.flexAttributes),
      defaultProductFamily: selectedCategory.defaultProductFamily || '',
    };
    try {
      await StageRepository.updateStageCategory(CRMSubmitObj);
      setStageCategories((p) => ({
        ...p,
        [selectedCategory.id]: selectedCategory,
      }));
      setCopySelectedCategory(selectedCategory);
    } catch (err) {
      console.log('ERROR updating CRM', err);
    }
  };

  const handleUpdateStages = (stages) => {
    const updatedCRM = { ...selectedCategory };
    updatedCRM.stages = stages;
    setStageCategories((p) => ({
      ...p,
      [updatedCRM.id]: updatedCRM,
    }));
    setCopySelectedCategory(updatedCRM);
    setSelectedCategory(updatedCRM);
  };

  const handleDispositionUpdates = (dispositions) => {
    const updatedCRM = { ...selectedCategory };
    updatedCRM.dispositions = dispositions;
    setStageCategories((p) => ({
      ...p,
      [updatedCRM.id]: updatedCRM,
    }));
    setCopySelectedCategory(updatedCRM);
    setSelectedCategory(updatedCRM);
  };

  const handleDealCardConfigUpdate = async (dealCardConfig) => {
    const updatedCRM = { ...selectedCategory };

    // Store deal card config inside flexAttributes.dealCard
    updatedCRM.flexAttributes.dealCard = dealCardConfig;

    // Save to backend
    const CRMSubmitObj = {
      id: selectedCategory.id,
      isDefault: selectedCategory.isDefault,
      title: selectedCategory.title,
      enableDeal: selectedCategory.enableDeal,
      enablePindrop: selectedCategory.enablePindrop,
      flexAttributes: JSON.stringify(updatedCRM.flexAttributes),
      defaultProductFamily: selectedCategory.defaultProductFamily || '',
    };

    try {
      await StageRepository.updateStageCategory(CRMSubmitObj);
      setStageCategories((p) => ({
        ...p,
        [updatedCRM.id]: updatedCRM,
      }));
      setCopySelectedCategory(updatedCRM);
      setSelectedCategory(updatedCRM);
    } catch (err) {
      console.log('ERROR updating deal card config', err);
    }
  };

  const getDefaultDealCardConfig = () => ({
    layout: {
      header: {
        name: { field: 'name', enabled: true, position: 0 },
        priority: { field: 'priority', enabled: true, position: 1 },
      },
      image: {
        photo: { field: 'photo', enabled: true, position: 0 },
      },
      contact: {
        phone: { field: 'phone', enabled: true, position: 0 },
        email: { field: 'email', enabled: true, position: 1 },
      },
      people: {
        setter: { field: 'setter', enabled: true, position: 0 },
        closer: { field: 'closer', enabled: true, position: 1 },
      },
      date: {
        followUp: { field: 'followUpDate', enabled: true, position: 0 },
      },
      tags: {
        dealType: { field: 'dealType', enabled: true, position: 0 },
      },
      description: {
        notes: { field: 'description', enabled: true, position: 0 },
      },
    },
  });

  const handleCreateNewCRM = async (title) => {
    setCloningLoading(true);
    console.log(
      'Creating new CRM by cloning selected category:',
      selectedCategory
    );

    const newID = uuidv4();
    // Clone the selected category instead of using template
    const newCRM = {
      ...selectedCategory,
      title,
      id: newID,
      isDefault: false, // Ensure new CRM is not default
      flexAttributes: {
        ...selectedCategory.flexAttributes,
        rememberUserChoice: true, // Default to true for new CRMs
        defaultScreen: selectedCategory.flexAttributes?.defaultScreen || 'maps', // Default to maps for new CRMs
      },
    };

    console.log('New CRM object before submission:', newCRM);

    // Prepare submit object (same logic as before)
    const submitObject = { ...newCRM };
    delete submitObject.dispositions;
    delete submitObject.stages;
    delete submitObject.dependentKPIRules;
    submitObject.flexAttributes = JSON.stringify(submitObject.flexAttributes);

    console.log('Submit object for new CRM:', submitObject);

    try {
      // First, create the stage category
      await StageRepository.createStageCategory(submitObject);
      console.log('Stage category created successfully');

      // Then clone all stages
      if (selectedCategory.stages && selectedCategory.stages.length > 0) {
        console.log('=== STARTING STAGE CLONING ===');
        console.log('Total stages to clone:', selectedCategory.stages.length);

        for (let i = 0; i < selectedCategory.stages.length; i++) {
          const stage = selectedCategory.stages[i];
          console.log(
            `\n--- Cloning stage ${i + 1}/${selectedCategory.stages.length}: "${
              stage.title
            }" ---`
          );

          const newStageID = uuidv4();
          const stageToCreate = {
            id: newStageID,
            orgID,
            categoryID: newID,
            title: stage.title || '',
            description: stage.description || '',
            action: stage.action || '',
            actionPastTense: stage.actionPastTense || '',
            measure: stage.measure || '',
            measurePlural: stage.measurePlural || '',
            valueUnit: stage.valueUnit || '',
            valueUnitPlural: stage.valueUnitPlural || '',
            countUnit: stage.countUnit || '',
            countUnitPlural: stage.countUnitPlural || '',
            iconName: stage.iconName || '',
            iconType: stage.iconType || '',
            iconColor: stage.iconColor || '',
            flexAttributes: stage.flexAttributes || null,
            sequence: stage.sequence || 0,
            winProbability: stage.winProbability || 0,
            pinColor: stage.pinColor || '',
            icon: stage.icon || '',
            isMapDefault: stage.isMapDefault || false,
            isDisplay: stage.isDisplay || false,
            isActive: stage.isActive || false,
            isNoDealDefault: stage.isNoDealDefault || false,
            imageName: stage.imageName || '',
            imageType: stage.imageType || '',
            createdBy: '',
            lastUpdatedBy: '',
            createdAt: null,
            updatedAt: null,
            isDeleted: false,
          };

          // Only add calendarAppointmentStatus if it has a valid value (not null/empty)
          if (
            stage.calendarAppointmentStatus &&
            stage.calendarAppointmentStatus.trim() !== ''
          ) {
            stageToCreate.calendarAppointmentStatus =
              stage.calendarAppointmentStatus;
          }

          try {
            console.log(
              'Stage payload being sent:',
              JSON.stringify(stageToCreate, null, 2)
            );

            const stageResult = await API.graphql(
              graphqlOperation(createStage, { input: stageToCreate })
            );

            console.log(
              `✅ Stage "${stage.title}" created successfully:`,
              stageResult
            );
          } catch (stageError) {
            console.error(`❌ STAGE CLONING FAILED for "${stage.title}"`);

            GamifyToast.error(
              'Failed to clone stages',
              `Error creating stage "${stage.title}". Check console for details.`
            );
            return; // Halt the operation
          }
        }
        console.log('=== ALL STAGES CLONED SUCCESSFULLY ===');
      } else {
        console.log('No stages to clone');
      }

      // Then clone all dispositions
      if (
        selectedCategory.dispositions &&
        selectedCategory.dispositions.length > 0
      ) {
        for (let i = 0; i < selectedCategory.dispositions.length; i++) {
          const disposition = selectedCategory.dispositions[i];
          console.log(
            `\n--- Cloning disposition ${i + 1}/${
              selectedCategory.dispositions.length
            }: "${disposition.title}" ---`
          );

          const newDispositionID = uuidv4();
          const dispositionToCreate = {
            id: newDispositionID,
            orgID,
            categoryID: newID,
            title: disposition.title || '',
            isSetupDeal: disposition.isSetupDeal || false,
            requireDate: disposition.requireDate || false,
            deleteType: disposition.deleteType || '',
            order: disposition.order || 0,
            iconName: disposition.iconName || '',
            iconType: disposition.iconType || '',
            iconColor: disposition.iconColor || '',
            createdBy: '',
            lastUpdatedBy: '',
            createdAt: null,
            updatedAt: null,
            isDeleted: false,
          };

          try {
            await API.graphql(
              graphqlOperation(createStageDisposition, {
                input: dispositionToCreate,
              })
            );
          } catch (dispositionError) {
            GamifyToast.error(
              'Failed to clone dispositions',
              `Error creating disposition "${disposition.title}". Check console for details.`
            );
            return; // Halt the operation
          }
        }
        console.log('=== ALL DISPOSITIONS CLONED SUCCESSFULLY ===');
      } else {
        console.log('No dispositions to clone');
      }

      // Update local state with the complete cloned CRM
      setStageCategories((p) => ({ ...p, [newID]: newCRM }));
      setSelectedCategory(newCRM);
      setCopySelectedCategory(newCRM);

      const stageCount = selectedCategory.stages?.length || 0;
      const dispositionCount = selectedCategory.dispositions?.length || 0;

      GamifyToast.success(
        'CRM Cloned Successfully',
        `"${title}" created with ${stageCount} stages and ${dispositionCount} dispositions.`
      );
    } catch (err) {
      GamifyToast.error(
        'Failed to create CRM',
        'There was an error creating the new CRM. Please try again.'
      );
    } finally {
      setCloningLoading(false);
    }
  };
  const handleSwitchChange = (field, value) => {
    if (field === 'isDefault' && value) {
      const currentDefault = getDefaultCategory();
      if (currentDefault && currentDefault.title !== selectedCategory.title) {
        setDefaultCategoryName(currentDefault.title);
        setDialogOpen(true);
        return;
      }
    }
    setSelectedCategory((prev) => ({ ...prev, [field]: value }));
  };

  const handleRememberUserChoiceChange = (value) => {
    setSelectedCategory((prev) => {
      const updatedFlexAttributes = {
        ...prev.flexAttributes,
        rememberUserChoice: value,
      };
      return {
        ...prev,
        flexAttributes: updatedFlexAttributes,
      };
    });
  };

  const handleDefaultScreenChange = (value) => {
    setSelectedCategory((prev) => {
      const updatedFlexAttributes = {
        ...prev.flexAttributes,
        defaultScreen: value,
      };
      return {
        ...prev,
        flexAttributes: updatedFlexAttributes,
      };
    });
  };

  const handleDefaultPipelineViewChange = (value) => {
    setSelectedCategory((prev) => {
      const updatedFlexAttributes = {
        ...prev.flexAttributes,
        defaultPipelineView: value,
      };
      return {
        ...prev,
        flexAttributes: updatedFlexAttributes,
      };
    });
  };

  const handleUseSameFormForEditChange = (value) => {
    setSelectedCategory((prev) => {
      const updatedFlexAttributes = {
        ...prev.flexAttributes,
        useSameFormForEdit: value,
      };
      return {
        ...prev,
        flexAttributes: updatedFlexAttributes,
      };
    });
  };

  const ensureCategoryDefaults = (category) => {
    return {
      ...category,
      defaultProductFamily: category.defaultProductFamily || '',
      flexAttributes: {
        ...category.flexAttributes,
        rememberUserChoice: category.flexAttributes?.rememberUserChoice ?? true,
        defaultScreen: category.flexAttributes?.defaultScreen ?? 'maps',
        // Backward compatibility: default to list view for existing CRMs, enable toggle
        defaultPipelineView:
          category.flexAttributes?.defaultPipelineView ?? 'list',
        enableKanbanView: category.flexAttributes?.enableKanbanView ?? true,
      },
    };
  };

  const getDefaultCategory = () => {
    return Object.values(stageCategories).find(
      (category) => category.isDefault
    );
  };

  if (isLoading || !stageCategories) {
    return (
      <View>
        <CircularProgress />
      </View>
    );
  }

  if (cloningLoading) {
    return (
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
        }}
      >
        <CircularProgress />
        <Typography style={{ marginTop: '16px' }}>
          Cloning CRM with stages and dispositions...
        </Typography>
      </View>
    );
  }

  const noDefaultCategory = !Object.values(stageCategories).some(
    (category) => category.isDefault
  );

  return (
    <View>
      <View className="categories_selection_container">
        {Object.values(stageCategories).map((category) => (
          <CustomButton
            style={{
              backgroundColor:
                category.id === selectedCategory?.id ? '#FF6A00' : 'white',
            }}
            key={category.id}
            onClick={() => {
              setSelectedCategory(category);
              setCopySelectedCategory(category);
            }}
          >
            <Text
              className="category_selection_button_text"
              style={{
                fontSize: 'large',
                color: category.id === selectedCategory?.id ? 'white' : 'black',
              }}
            >
              {category.title}
            </Text>
          </CustomButton>
        ))}
        <CustomButtonWithInput
          takenNames={Object.values(stageCategories).map((cat) => cat.title)}
          onSubmit={handleCreateNewCRM}
          style={{ marginLeft: 'auto' }}
          disabled={cloningLoading}
          loading={cloningLoading}
          buttonText={`Create new CRM based from "${selectedCategory?.title}"`}
        />
      </View>
      {selectedCategory && (
        <React.Fragment>
          <View className="category_general_info_container">
            <View>
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Text
                    style={{ fontSize: 'large', fontWeight: 500 }}
                    onClick={() => console.log(selectedCategory)}
                  >
                    CRM Title
                  </Text>
                  <CustomTextField
                    value={selectedCategory?.title}
                    onChange={(e) =>
                      setSelectedCategory((p) => ({
                        ...p,
                        title: e.target.value,
                      }))
                    }
                  />
                </Box>
                <Box display="flex" gap={1}>
                  {unsavedChanges && (
                    <Button
                      color="primary"
                      variant="contained"
                      style={{ height: '45px' }}
                      onClick={handleCRMUpdates}
                    >
                      Save Changes
                    </Button>
                  )}
                </Box>
              </Box>
              {noDefaultCategory && (
                <Typography color="error" variant="body2">
                  No default category selected.
                </Typography>
              )}
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedCategory.enableDeal || false}
                      onChange={(e) =>
                        handleSwitchChange('enableDeal', e.target.checked)
                      }
                    />
                  }
                  label="Enable Deal"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedCategory.enablePindrop || false}
                      onChange={(e) =>
                        handleSwitchChange('enablePindrop', e.target.checked)
                      }
                    />
                  }
                  label="Enable Pindrop"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        selectedCategory.flexAttributes?.rememberUserChoice !==
                        false
                      }
                      onChange={(e) =>
                        handleRememberUserChoiceChange(e.target.checked)
                      }
                    />
                  }
                  label="Remember User's CRM Selection"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        selectedCategory.flexAttributes?.useSameFormForEdit ||
                        false
                      }
                      onChange={(e) =>
                        handleUseSameFormForEditChange(e.target.checked)
                      }
                    />
                  }
                  label="Use Same Form for Editing"
                />
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ ml: 4, mb: 1, color: 'text.secondary' }}
                >
                  {selectedCategory.flexAttributes?.useSameFormForEdit
                    ? 'Deals will always be edited using the same form they were created with, regardless of deal stage.'
                    : 'Deals will use the form assigned to their current disposition when editing (forms can expand as deals progress).'}
                </Typography>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Default Screen
                  </Typography>
                  <Select
                    value={
                      selectedCategory.flexAttributes?.defaultScreen || 'maps'
                    }
                    onChange={(e) => handleDefaultScreenChange(e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="maps" sx={menuItemStyles}>
                      Maps
                    </MenuItem>
                    <MenuItem value="pipeline" sx={menuItemStyles}>
                      Pipeline
                    </MenuItem>
                  </Select>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 1, color: 'text.secondary' }}
                  >
                    Choose the default screen when users select this CRM in the
                    Field tab
                  </Typography>
                </Box>
                <Box
                  border={1}
                  borderColor="grey.500"
                  borderRadius={4}
                  p={2}
                  mt={2}
                >
                  <Typography variant="body2" color="textSecondary">
                    Only one category can be set as default. Changing the
                    default category will require setting the current default to
                    non-default first.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedCategory.isDefault || false}
                        onChange={(e) =>
                          handleSwitchChange('isDefault', e.target.checked)
                        }
                      />
                    }
                    label="Set as Default"
                  />
                </Box>

                {/* Kanban View Settings */}
                <Box
                  border={1}
                  borderColor="grey.500"
                  borderRadius={4}
                  p={2}
                  mt={2}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Pipeline View Settings
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    Configure the default view and available options for the
                    pipeline screen.
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Default Pipeline View
                    </Typography>
                    <Select
                      value={
                        selectedCategory.flexAttributes?.defaultPipelineView ||
                        'list'
                      }
                      onChange={(e) =>
                        handleDefaultPipelineViewChange(e.target.value)
                      }
                      size="small"
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="list" sx={menuItemStyles}>
                        List View
                      </MenuItem>
                      <MenuItem value="kanban" sx={menuItemStyles}>
                        Kanban View
                      </MenuItem>
                    </Select>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 1, color: 'text.secondary' }}
                    >
                      Choose the default view when users open the pipeline
                      screen (this is enforced when dealsKanban feature flag is
                      enabled)
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* DocuSeal Settings */}
              {selectedCategory && (
                <Box
                  className="multi-att-feature-box"
                  style={{ marginTop: '20px' }}
                >
                  <Box className="flex-centered space-b">
                    <Text
                      style={{
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        try {
                          const configParms = JSON.parse(org.configParms);
                          if (!configParms.publish) {
                            configParms.publish = {};
                          }
                          configParms.publish.docuSeal = !docuSealEnabled;
                          setOrg({
                            ...org,
                            configParms: JSON.stringify(configParms),
                          });
                          setDocuSealEnabled(!docuSealEnabled);
                        } catch (e) {
                          console.error(
                            'Error updating docuSeal enabled flag',
                            e
                          );
                        }
                      }}
                    >
                      DocuSeal
                    </Text>
                    <Switch
                      checked={docuSealEnabled}
                      onChange={() => {
                        try {
                          const configParms = JSON.parse(org.configParms);
                          if (!configParms.publish) {
                            configParms.publish = {};
                          }
                          configParms.publish.docuSeal = !docuSealEnabled;
                          setOrg({
                            ...org,
                            configParms: JSON.stringify(configParms),
                          });
                          setDocuSealEnabled(!docuSealEnabled);
                        } catch (e) {
                          console.error(
                            'Error updating docuSeal enabled flag',
                            e
                          );
                        }
                      }}
                    />
                  </Box>
                  {docuSealEnabled && (
                    <Box>
                      <Box className="flex-centered space-b g-hov">
                        <Text
                          style={{
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                          onClick={() => apiKeyInputRef.current?.focus()}
                        >
                          DocuSeal API Key
                        </Text>
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <TextField
                            ref={apiKeyInputRef}
                            type="password"
                            value={docuSealConfig.apiKey}
                            onChange={(e) =>
                              setDocuSealConfig((prev) => ({
                                ...prev,
                                apiKey: e.target.value,
                              }))
                            }
                            size="small"
                          />
                          <DocuSealInstructions orgID={orgID} />
                        </Box>
                      </Box>

                      <Box className="flex-centered space-b g-hov">
                        <Text style={{ fontWeight: 'bold' }}>
                          Default Product Family
                        </Text>
                        <Select
                          value={selectedCategory.defaultProductFamily || ''}
                          onChange={(e) => {
                            setSelectedCategory((prev) => ({
                              ...prev,
                              defaultProductFamily: e.target.value,
                            }));
                          }}
                          displayEmpty
                          size="small"
                          sx={{ minWidth: 200 }}
                        >
                          <MenuItem value="" sx={menuItemStyles}>
                            <em>None</em>
                          </MenuItem>
                          {docuSealFolders.map((folder) => (
                            <MenuItem
                              key={folder}
                              value={folder}
                              sx={menuItemStyles}
                            >
                              {folder}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>

                      <Box className="flex-centered space-b g-hov">
                        <Text
                          style={{
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            setDocuSealConfig((prev) => ({
                              ...prev,
                              pdfEnabled: !prev.pdfEnabled,
                            }))
                          }
                        >
                          PDF Enabled
                        </Text>
                        <Switch
                          checked={docuSealConfig.pdfEnabled}
                          onChange={(e) =>
                            setDocuSealConfig((prev) => ({
                              ...prev,
                              pdfEnabled: e.target.checked,
                            }))
                          }
                        />
                      </Box>

                      <Box className="flex-centered space-b g-hov">
                        <Text
                          style={{
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            handleSigningMethodChange(
                              'signOnDevice',
                              !docuSealConfig.signOnDevice
                            )
                          }
                        >
                          Sign On Device
                        </Text>
                        <Switch
                          checked={docuSealConfig.signOnDevice}
                          onChange={(e) =>
                            handleSigningMethodChange(
                              'signOnDevice',
                              e.target.checked
                            )
                          }
                        />
                      </Box>

                      <Box className="flex-centered space-b g-hov">
                        <Text
                          style={{
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            handleSigningMethodChange(
                              'signUsingEmail',
                              !docuSealConfig.signUsingEmail
                            )
                          }
                        >
                          Sign Using Email
                        </Text>
                        <Switch
                          checked={docuSealConfig.signUsingEmail}
                          onChange={(e) =>
                            handleSigningMethodChange(
                              'signUsingEmail',
                              e.target.checked
                            )
                          }
                        />
                      </Box>

                      {selectedCategory.flexAttributes?.config &&
                        Object.values(
                          selectedCategory.flexAttributes.config
                        ).some((form) =>
                          form.flexFields?.some(
                            (field) => field.type === 'Order Lines'
                          )
                        ) && (
                          <Box className="flex-centered space-b g-hov">
                            <Text
                              style={{
                                fontWeight: 'bold',
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                setDocuSealConfig((prev) => ({
                                  ...prev,
                                  noOrderLinesRequiredForSigning:
                                    !prev.noOrderLinesRequiredForSigning,
                                }))
                              }
                            >
                              No Order Lines Required for Signing
                            </Text>
                            <Switch
                              checked={
                                docuSealConfig.noOrderLinesRequiredForSigning
                              }
                              onChange={(e) =>
                                setDocuSealConfig((prev) => ({
                                  ...prev,
                                  noOrderLinesRequiredForSigning:
                                    e.target.checked,
                                }))
                              }
                            />
                          </Box>
                        )}
                      <Box style={{ marginTop: '20px' }}>
                        <Text
                          style={{ fontWeight: 'bold', marginBottom: '10px' }}
                        >
                          Stage Mapping
                        </Text>
                        {selectedCategory && (
                          <Box
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '10px',
                            }}
                          >
                            <Box className="flex-centered space-b">
                              <Text>Awaiting Stage</Text>
                              <Select
                                value={stageMapping?.awaiting || ''}
                                onChange={(e) =>
                                  setStageMapping((prev) => ({
                                    ...prev,
                                    awaiting: e.target.value,
                                  }))
                                }
                                displayEmpty
                              >
                                <MenuItem value="" sx={menuItemStyles}>
                                  <em>Select a stage</em>
                                </MenuItem>
                                {(selectedCategory.stages || []).map(
                                  (stage) => (
                                    <MenuItem
                                      key={stage.id}
                                      value={stage.id}
                                      sx={menuItemStyles}
                                    >
                                      {stage.title}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </Box>
                            <Box className="flex-centered space-b">
                              <Text>Completed Stage</Text>
                              <Select
                                value={stageMapping?.completed || ''}
                                onChange={(e) =>
                                  setStageMapping((prev) => ({
                                    ...prev,
                                    completed: e.target.value,
                                  }))
                                }
                                displayEmpty
                              >
                                <MenuItem value="" sx={menuItemStyles}>
                                  <em>Select a stage</em>
                                </MenuItem>
                                {(selectedCategory.stages || []).map(
                                  (stage) => (
                                    <MenuItem
                                      key={stage.id}
                                      value={stage.id}
                                      sx={menuItemStyles}
                                    >
                                      {stage.title}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </Box>
                            <Box className="flex-centered space-b">
                              <Text>Default Stage</Text>
                              <Select
                                value={stageMapping?.default || ''}
                                onChange={(e) =>
                                  setStageMapping((prev) => ({
                                    ...prev,
                                    default: e.target.value,
                                  }))
                                }
                                displayEmpty
                              >
                                <MenuItem value="" sx={menuItemStyles}>
                                  <em>Select a stage</em>
                                </MenuItem>
                                {(selectedCategory.stages || []).map(
                                  (stage) => (
                                    <MenuItem
                                      key={stage.id}
                                      value={stage.id}
                                      sx={menuItemStyles}
                                    >
                                      {stage.title}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box style={{ marginTop: '20px' }}>
                        <Button
                          variant="contained"
                          onClick={saveDocuSealConfig}
                        >
                          Save DocuSeal Configuration
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {/* End of DocuSeal Settings */}
            </View>
          </View>
          <Box style={{ flexDirection: 'row', display: 'flex' }}>
            <Box
              onClick={() => setConfigPage('stages')}
              className="stage_form_selector"
              style={{
                backgroundColor: configPage === 'stages' ? '#f0f0f0' : 'white',
              }}
            >
              <Typography
                style={{
                  color: configPage === 'stages' ? '#000' : '#888',
                  fontWeight: configPage === 'stages' ? 'bold' : 'normal',
                }}
              >
                Deal Stages
              </Typography>
            </Box>
            <Box
              onClick={() => setConfigPage('forms')}
              className="stage_form_selector"
              style={{
                backgroundColor: configPage === 'forms' ? '#f0f0f0' : 'white',
              }}
            >
              <Typography
                style={{
                  color: configPage === 'forms' ? '#000' : '#888',
                  fontWeight: configPage === 'forms' ? 'bold' : 'normal',
                }}
              >
                Forms
              </Typography>
            </Box>
            <Box
              onClick={() => setConfigPage('dispositions')}
              className="stage_form_selector"
              style={{
                backgroundColor:
                  configPage === 'dispositions' ? '#f0f0f0' : 'white',
              }}
            >
              <Typography
                style={{
                  color: configPage === 'dispositions' ? '#000' : '#888',
                  fontWeight: configPage === 'dispositions' ? 'bold' : 'normal',
                }}
              >
                Dispositions
              </Typography>
            </Box>
            <Box
              onClick={() => setConfigPage('dealcards')}
              className="stage_form_selector"
              style={{
                backgroundColor:
                  configPage === 'dealcards' ? '#f0f0f0' : 'white',
              }}
            >
              <Typography
                style={{
                  color: configPage === 'dealcards' ? '#000' : '#888',
                  fontWeight: configPage === 'dealcards' ? 'bold' : 'normal',
                }}
              >
                Deal Cards
              </Typography>
            </Box>
          </Box>
          {configPage === 'stages' && (
            <DealStagesEditor
              dealStages={selectedCategory.stages}
              kpiDependentStageTitles={selectedCategory.dependentKPIRules}
              stageDispositions={selectedCategory.dispositions}
              updateStages={handleUpdateStages}
              orgID={orgID}
              categoryID={selectedCategory.id}
            />
          )}
          {configPage === 'forms' && (
            <DealFormEditor
              key="DealEditor"
              dealForms={selectedCategory.flexAttributes.config}
              stages={selectedCategory.stages}
              stageDispositions={selectedCategory.dispositions}
              updateForms={handleFormUpdates}
            />
          )}
          {configPage === 'dispositions' && (
            <StageDispositionsEditor
              dispositions={selectedCategory.dispositions}
              stageForms={Object.keys(selectedCategory.flexAttributes.config)}
              stages={selectedCategory.stages}
              updateDispositions={handleDispositionUpdates}
              orgID={orgID}
              categoryID={selectedCategory.id}
            />
          )}
          {configPage === 'dealcards' && (
            <DealCardEditor
              dealCardConfig={
                selectedCategory.flexAttributes.dealCard ||
                getDefaultDealCardConfig()
              }
              flexFields={(() => {
                // Standard deal fields
                const standardFields = [
                  'name',
                  'priority',
                  'phone',
                  'email',
                  'setter',
                  'closer',
                  // 'followUpDate',
                  // 'dealType',
                  'description',
                  // Spacing fields for layout
                  'spacing-small',
                  'spacing-medium',
                  'spacing-large',
                  'spacing-custom',
                ];

                // Flex fields from all form configurations (display fields only)
                const displayFieldsResult = (() => {
                  const allDisplayFields = [];

                  // Check both locations: selectedCategory.config and selectedCategory.flexAttributes.config
                  const configSources = [
                    {
                      name: 'selectedCategory.config',
                      data: selectedCategory.config,
                    },
                    {
                      name: 'selectedCategory.flexAttributes.config',
                      data: selectedCategory.flexAttributes?.config,
                    },
                  ];

                  configSources.forEach(({ data }) => {
                    if (data) {
                      Object.keys(data).forEach((configName) => {
                        const configData = data[configName];

                        if (
                          configData?.displayFields &&
                          Array.isArray(configData.displayFields)
                        ) {
                          allDisplayFields.push(...configData.displayFields);
                        }
                      });
                    }
                  });

                  // Remove duplicates
                  return [...new Set(allDisplayFields)];
                })();

                const allFields = [...standardFields, ...displayFieldsResult];
                return allFields;
              })()}
              updateDealCardConfig={handleDealCardConfigUpdate}
            />
          )}
        </React.Fragment>
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Change Default Category'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`The category "${defaultCategoryName}" is currently set as the
            default. Please set it to non-default before setting another
            category as the default.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </View>
  );
};

function DocuSealInstructions({ orgID }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <InfoIcon style={{ cursor: 'pointer' }} onClick={() => setOpen(true)} />
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>DocuSeal Setup Instructions</DialogTitle>
        <DialogContent>
          <Box>
            <ol>
              <li>
                Navigate to:{' '}
                <a
                  href="https://docuseal.com/sign_up"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'blue' }}
                >
                  https://docuseal.com/sign_up
                </a>
              </li>
              <li>
                Create an email with{' '}
                <a
                  href={`mailto:david+${orgID}@gogamify.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'blue' }}
                >
                  david+{orgID}@gogamify.com
                </a>
              </li>
              <li>
                Click the Settings link on the upper right corner of the screen
              </li>
              <li>Click on the API option on the sidebar</li>
              <li>Copy the API key and paste it into the API Key field</li>
              <li>
                Click on Webhooks on the sidebar and paste the following URL
                into the Webhook URL field:
              </li>
              <li>
                <a
                  href={`https://api.gogamify.com/docuseal/v1/webhook/${orgID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'blue' }}
                >
                  https://api.gogamify.com/docuseal/v1/webhook/{orgID}
                </a>
              </li>
            </ol>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CRMConfigV2;
